import { createSelector } from 'reselect'
import { get, uniqWith } from 'lodash'
import { IStore } from '../index'
import {
  getEvents,
  notInRemovedAndRemovedRooms,
} from '../bookingCalendar/selectors/events'
import { ClientOrder } from './index'

export const getClientsListLoading = (store: IStore) => store.clients.isLoading
export const getClientsListPagination = (store: IStore) =>
  store.clients.pagination
export const getClientsListPage = (store: IStore) => store.clients.page
export const getClientsList = createSelector(
  [notInRemovedAndRemovedRooms],
  events => {
    const clients = events.filter(e =>
      e.guest ? Boolean(e.guest.name || e.guest.phone || e.guest.email) : false,
    )

    const mappedClients = clients.map(e => ({
      email: e.guest.email,
      lcemail: String(e.guest.email).toLowerCase(),
      lcname: String(e.guest.name).toLowerCase(),
      name: e.guest.name,
      phone: e.guest.phone,
    }))

    return uniqWith(
      mappedClients,
      (c, client) =>
        c.lcname === client.lcname &&
        c.phone === client.phone &&
        c.lcemail === client.lcemail,
    ).map((c, i) => ({ ...c, id: i + 1 }))
  },
)

export const getClientsListSearch = createSelector(
  [notInRemovedAndRemovedRooms],
  events => {
    const clients = events.filter(event =>
      event.guest
        ? Boolean(
        event.guest.phone !== '' ||
        (event.guest.city !== '' &&
          (event.guest.name !== '' || event.guest.phone !== '')) ||
        event.guest.name !== '',
        )
        : false,
    )

    const mappedClients = clients.map(e => ({
      lccity: String(e.guest.city).toLowerCase(),
      lcemail: String(e.guest.email).toLowerCase(),
      lcname: String(e.guest.name).toLowerCase(),
      ...e.guest,
    }))

    return uniqWith(
      mappedClients,
      (c, client) =>
        c.lcname === client.lcname &&
        c.phone === client.phone &&
        c.lccity === client.lccity,
    ).map((c, i) => ({ ...c, id: i + 1 }))
  },
)
export const getClientsListFilter = (store: IStore) => store.clients.filter
export const getClientListBy = (store: IStore) => store.clients.by
export const getClientListDirection = (store: IStore) => store.clients.direction
export const getClientListModalOpen = (store: IStore) => store.clients.isOpen
export const getClientListModalId = (store: IStore) => store.clients.id
export const getClientByListId = createSelector(
  [getClientsList, getClientListModalId],
  (clients, id) => clients.find(c => c.id === id),
)
export const getClientListReservations = (store: IStore) =>
  store.clients.reservations

export const getClientsListFiltered = createSelector(
  [getClientsList, getClientsListFilter],
  (clients, filter) =>
    filter.length >= 3
      ? clients.filter(c =>
        ['name', 'phone', 'email']
          .map(path => get(c, path, ''))
          .some(v =>
            v
              .toString()
              .toLocaleLowerCase()
              .includes(filter.toLocaleLowerCase()),
          ),
      )
      : clients,
)

export const getClientsListMaxPage = createSelector(
  [getClientsListFiltered, getClientsListPagination],
  (clients, pagination) => Math.ceil(clients.length / pagination) || 1,
)

export const getClientListOrdered = createSelector(
  [getClientsListFiltered, getClientListBy, getClientListDirection],
  (clients, by, direction) => {
    switch (by) {
      case ClientOrder.Id:
        return [...sortByNumber(clients, direction, 'id')]
      case ClientOrder.Name:
        const narr = clients.filter(c => !!c.name)
        const wnarr = sortByNumber(
          clients.filter(c => !c.name),
          direction,
          'id',
        )

        return sortByString(narr, direction, 'name').concat(wnarr)
      case ClientOrder.Phone:
        return clients
      case ClientOrder.Email:
        const earr = clients.filter(c => !!c.email)
        const wearr = sortByNumber(
          clients.filter(c => !c.email),
          direction,
          'id',
        )

        return sortByString(earr, direction, 'email').concat(wearr)
      default:
        return clients
    }
  },
)

export const getClientListPaginated = createSelector(
  [getClientListOrdered, getClientsListPage, getClientsListPagination],
  (clients, page, pagination) =>
    clients.slice(
      pagination * (page - 1),
      pagination * (page - 1) + pagination,
    ),
)

function sortByNumber(clients, direction, field) {
  if (direction === 'up') {
    return clients.sort((a, b) => a[field] - b[field])
  } else {
    return clients.sort((a, b) => b[field] - a[field])
  }
}

function sortByString(clients, direction, field) {
  if (direction === 'up') {
    return clients.sort((a, b) => a[field].localeCompare(b[field]))
  } else {
    return clients.sort((a, b) => a[field].localeCompare(b[field]) * -1)
  }
}

export const searchClients = createSelector(
  [getEvents],
  events =>
    events.filter(
      event =>
        event.guest.phone !== '' ||
        (event.guest.city !== '' &&
          (event.guest.name !== '' || event.guest.phone !== '')) ||
        event.guest.name !== '',
    ),
)
